import { useEffect, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { MdClose } from "react-icons/md";
import { HiOutlineEye } from "react-icons/hi";
import moment from "moment";
import { toast } from "react-toastify";
import { handleError } from "src/Common/function";
import ReportService from "src/Services/API/report.service";
import { formatDate } from "src/Common/common";
import { Oval } from "react-loader-spinner";
import DateRangeSelector, { DATE_RANGE_OPTIONS } from "../components/DateRangeSelector";
import RecipientsChipInput from "../components/RecipientsChipInput";
import ReportGeneratedModal from "../components/ReportGeneratedModal";
import ReportFiltersSection, { EMPTY_REPORT_FILTERS } from "../components/ReportFiltersSection";

// Ad-hoc reports we can send from this modal and how they map to the report service.
export const AD_HOC_REPORT_TYPES = [
  { name: "Open Work Orders", value: 0, kind: "WO", woType: "OPEN_WO" },
  { name: "Completed Work Orders", value: 1, kind: "WO", woType: "COMPLETED_WO" },
  { name: "Time Card", value: 20, kind: "HOURS_WORKED" },
];

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Columns shown in the preview table, keyed by report kind. `key` matches the
// flat row shape returned by the backend preview endpoints. `track` is the CSS
// grid column size; a "1fr" column flexes to fill the remaining width.
const PREVIEW_COLUMNS = {
  WO: [
    { header: "WO #", key: "number", track: "120px", strong: true },
    { header: "Type", key: "type", track: "140px" },
    { header: "Customer", key: "customer", track: "1fr", truncate: true },
    { header: "Primary Tech", key: "primaryTech", track: "160px", truncate: true },
    { header: "Status", key: "status", track: "140px" },
  ],
  HOURS_WORKED: [
    { header: "Technician", key: "technician", track: "160px", strong: true, truncate: true },
    { header: "Customer", key: "customer", track: "1fr", truncate: true },
    { header: "WO #", key: "woNumber", track: "110px" },
    { header: "Date", key: "date", track: "110px" },
    { header: "Regular", key: "regularTime", track: "90px" },
    { header: "Travel", key: "travelTime", track: "90px" },
    { header: "Over", key: "overTime", track: "90px" },
  ],
};

/**
 * "Generate Report" modal for ad-hoc reports (new design).
 *
 * shown / onClose: visibility control
 * userArray: enabled users (for resolving recipient chips)
 * reportType: report-type value this modal generates (defaults to Open Work Orders)
 * onSent: called after a report is successfully sent
 */
const GenerateReportModal = ({ shown, onClose, userArray = [], reportType = 0, onSent }) => {
  const [step, setStep] = useState("filters"); // "filters" | "preview"
  // Customer/Branch/Location/Technician filters; empty arrays mean "all".
  const [filters, setFilters] = useState(EMPTY_REPORT_FILTERS);
  const [dateRange, setDateRange] = useState("past30");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [recipients, setRecipients] = useState([]); // [{ id, name, email, image }]
  const [preview, setPreview] = useState(null); // { recordsFound, rows }
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  // Holds the generated file once a report is sent; presence drives the success modal.
  const [generated, setGenerated] = useState(null); // { fileUrl, fileName }

  useEffect(() => {
    if (shown) {
      setStep("filters");
      setFilters(EMPTY_REPORT_FILTERS);
      setDateRange("past30");
      setCustomStart(null);
      setCustomEnd(null);
      setRecipients([]);
      setPreview(null);
      setIsPreviewing(false);
      setIsSending(false);
      setGenerated(null);
    }
  }, [shown]);

  const selectedType = AD_HOC_REPORT_TYPES.find((type) => type.value === reportType) || AD_HOC_REPORT_TYPES[0];

  // If the user switches to a type that doesn't support "All", drop that selection.
  useEffect(() => {
    if (selectedType.value !== 0 && dateRange === "all") {
      setDateRange("past30");
    }
  }, [selectedType.value, dateRange]);

  const addRecipient = (email) => {
    if (!isValidEmail(email)) {
      toast("Please enter a valid email address");
      return;
    }
    if (recipients.some((recipient) => recipient.email?.toLowerCase() === email.toLowerCase())) {
      return;
    }
    const matchedUser = userArray.find((user) => user.email?.toLowerCase() === email.toLowerCase());
    setRecipients((prev) => [
      ...prev,
      matchedUser
        ? { id: matchedUser.id, name: matchedUser.name, email: matchedUser.email, avatar: matchedUser.avatar }
        : { email },
    ]);
  };

  const removeRecipient = (recipient) => {
    setRecipients((prev) =>
      prev.filter((item) => (item.id ?? item.email) !== (recipient.id ?? recipient.email))
    );
  };

  // Selectable users that carry an email (needed for the recipients dropdown).
  const selectableUsers = userArray.filter((user) => user.email);

  const toggleRecipient = (user) => {
    setRecipients((prev) => {
      const exists = prev.some((item) => (item.id ?? item.email) === (user.id ?? user.email));
      if (exists) {
        return prev.filter((item) => (item.id ?? item.email) !== (user.id ?? user.email));
      }
      return [...prev, { id: user.id, name: user.name, email: user.email, avatar: user.avatar }];
    });
  };

  const selectAllRecipients = (checked) => {
    if (checked) {
      setRecipients(
        selectableUsers.map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          avatar: user.avatar,
        }))
      );
    } else {
      setRecipients([]);
    }
  };

  // "All" (no date filter) is only supported by the Open Work Orders report.
  const supportsAllOption = selectedType.value === 0;

  // Resolve the new date-range UI down to the flags the report service expects.
  const resolveDateFlags = () => {
    if (dateRange === "all") {
      return { all: true, past7Days: false, past30Days: false, customDays: false, startDate: null, endDate: null };
    }
    if (dateRange === "past7") {
      return { all: false, past7Days: true, past30Days: false, customDays: false, startDate: null, endDate: null };
    }
    if (dateRange === "past30") {
      return { all: false, past7Days: false, past30Days: true, customDays: false, startDate: null, endDate: null };
    }
    if (dateRange === "pastQuarter") {
      // No dedicated flag — express as a custom 90-day range.
      return {
        all: false,
        past7Days: false,
        past30Days: false,
        customDays: true,
        startDate: moment().subtract(90, "days").toDate(),
        endDate: new Date(),
      };
    }
    // custom
    return {
      all: false,
      past7Days: false,
      past30Days: false,
      customDays: true,
      startDate: customStart,
      endDate: customEnd,
    };
  };

  // Validate filters and resolve the API arguments shared by preview + send.
  const buildRequestArgs = () => {
    const flags = resolveDateFlags();
    if (flags.customDays && (!flags.startDate || !flags.endDate)) {
      toast("Please choose a start and end date");
      return null;
    }
    return {
      all: flags.all,
      past7Days: flags.past7Days,
      past30Days: flags.past30Days,
      customDays: flags.customDays,
      customStartDate: formatDate(flags.startDate, "MM/DD/YYYY"),
      customEndDate: formatDate(flags.endDate, "MM/DD/YYYY"),
      // Branch scoping is governed entirely by the `branchIds` filter array
      // (empty array = "All Branches"). The legacy single-branch param is gone.
      // Customer/Branch/Location/Technician filters (empty arrays = "all").
      filters: {
        customerIds: filters.customerIds,
        branchIds: filters.branchIds,
        locationIds: filters.locationIds,
        technicianIds: filters.technicianIds,
      },
    };
  };

  const loadPreview = () => {
    const args = buildRequestArgs();
    if (!args) return;
    setIsPreviewing(true);

    const request =
      selectedType.kind === "HOURS_WORKED"
        ? ReportService.getHoursWorkedReportPreview(
            0,
            args.past7Days,
            args.past30Days,
            args.customDays,
            args.customStartDate,
            args.customEndDate,
            formatDate(new Date(), "MM/DD/YYYY"),
            args.filters
          )
        : ReportService.getWOReportPreview(
            args.all,
            args.past7Days,
            args.past30Days,
            args.customDays,
            args.customStartDate,
            args.customEndDate,
            formatDate(new Date(), "MM/DD/YYYY"),
            selectedType.woType,
            args.filters
          );

    request
      .then((response) => {
        setPreview(response.data || { recordsFound: 0, rows: [] });
        setStep("preview");
      })
      .catch((error) => handleError(error))
      .finally(() => setIsPreviewing(false));
  };

  const sendReport = () => {
    const recipientIds = recipients.filter((recipient) => recipient.id != null).map((recipient) => String(recipient.id));

    if (recipientIds.length === 0) {
      toast("Please select at least one recipient that is a user in this branch");
      return;
    }

    const args = buildRequestArgs();
    if (!args) return;

    setIsSending(true);

    const request =
      selectedType.kind === "HOURS_WORKED"
        ? ReportService.sendHoursWorkedReport(
            0,
            recipientIds,
            args.past7Days,
            args.past30Days,
            args.customDays,
            args.customStartDate,
            args.customEndDate,
            new Date(),
            args.filters
          )
        : ReportService.sendWOReport(
            recipientIds,
            args.all,
            args.past7Days,
            args.past30Days,
            args.customDays,
            args.customStartDate,
            args.customEndDate,
            new Date(),
            selectedType.woType,
            args.filters
          );

    request
      .then((response) => {
        toast("Your report has been sent successfully");
        onSent?.();
        const data = response?.data || {};
        setGenerated({ fileUrl: data.fileUrl, fileName: data.fileName, fileSize: data.fileSize });
      })
      .catch((error) => handleError(error))
      .finally(() => setIsSending(false));
  };

  // Close both the success modal and the parent modal.
  const handleSuccessClose = () => {
    setGenerated(null);
    onClose();
  };

  if (!shown) return null;

  // After a successful send, swap the form for the "Report Generated" modal.
  if (generated) {
    return (
      <ReportGeneratedModal
        shown
        onClose={handleSuccessClose}
        fileUrl={generated.fileUrl}
        fileName={generated.fileName || `${selectedType.name} Report.xlsx`}
        fileSize={generated.fileSize}
      />
    );
  }

  // Display label for the selected date range (used in preview summary).
  const dateRangeLabel = (() => {
    if (dateRange === "all") return "All";
    if (dateRange === "custom") {
      return customStart && customEnd
        ? `${moment(customStart).format("MMM D")} - ${moment(customEnd).format("MMM D, YYYY")}`
        : "Custom Range";
    }
    const option = DATE_RANGE_OPTIONS.find((item) => item.key === dateRange);
    const end = moment();
    const start = moment().subtract(option?.days || 30, "days");
    return `${option?.label} (${start.format("MMM D")} - ${end.format("MMM D")})`;
  })();

  // Count-based labels for the selected filters in the preview summary.
  const filterSummary = (selectedIds, allLabel, singular) =>
    selectedIds.length === 0 ? allLabel : `${selectedIds.length} ${singular}${selectedIds.length === 1 ? "" : "s"}`;

  const columns = PREVIEW_COLUMNS[selectedType.kind] || PREVIEW_COLUMNS.WO;
  const previewRows = preview?.rows?.slice(0, 5) ?? [];

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30">
      <OutsideClickHandler
        onOutsideClick={(event) => {
          // Ignore clicks inside the recipients / filter dropdowns, which are
          // portaled to document.body and therefore count as "outside" this modal.
          if (event.target.closest?.("[data-recipients-dropdown],[data-report-dropdown]")) return;
          onClose();
        }}
      >
        <div
          className={`${
            step === "preview" ? "w-[1000px]" : "w-[640px]"
          } max-h-[90vh] overflow-auto bg-white rounded-[12px] shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04),0px_20px_25px_-5px_rgba(0,0,0,0.10)]`}
        >
          {/* Header */}
          <div
            className={`flex items-start gap-3 border-b-[1px] border-[#E5E8F3] ${
              step === "preview" ? "px-8 py-6" : "px-6 py-4"
            }`}
          >
            <div className="flex-1 flex flex-col gap-1">
              <p
                className={`font-OpenSansSemiBold capitalize text-[#1A2332] ${
                  step === "preview" ? "text-[24px] leading-[32px]" : "text-[20px] leading-[28px]"
                }`}
              >
                {step === "filters" ? `Generate ${selectedType.name} Report` : `${selectedType.name} Report Preview`}
              </p>
              <p
                className={`font-OpenSansRegular tracking-[0.14px] text-[#7C829A] ${
                  step === "preview" ? "text-[16px] leading-[24px]" : "text-[14px] leading-[20px]"
                }`}
              >
                {step === "filters"
                  ? "Configure filters and export report data."
                  : "Review the report before sending it to recipients."}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#868DA6] hover:text-[#1A2332]"
            >
              <MdClose size={18} />
            </button>
          </div>

          {step === "filters" ? (
            <>
              {/* Body */}
              <div className="px-8 py-8 flex flex-col gap-8">
                <ReportFiltersSection
                  value={filters}
                  onChange={setFilters}
                  shown={shown}
                />

                <div className="flex flex-col gap-3">
                  <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] capitalize">
                    Date Range
                  </p>
                  <DateRangeSelector
                    selected={dateRange}
                    onSelect={setDateRange}
                    includeAllOption={supportsAllOption}
                    customStart={customStart}
                    customEnd={customEnd}
                    onCustomChange={([start, end]) => {
                      setCustomStart(start);
                      setCustomEnd(end);
                    }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <RecipientsChipInput
                    recipients={recipients}
                    users={selectableUsers}
                    onAdd={addRecipient}
                    onToggle={toggleRecipient}
                    onSelectAll={selectAllRecipients}
                    onRemove={removeRecipient}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-4 px-6 py-4 border-t-[1px] border-[#E5E8F3]">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-10 px-1 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] text-[#7C829A] capitalize"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={loadPreview}
                  disabled={isPreviewing}
                  className={`h-10 px-4 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] flex items-center justify-center gap-2 min-w-[120px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] ${
                    isPreviewing ? "cursor-not-allowed opacity-80" : "hover:bg-[#F7F9FC]"
                  }`}
                >
                  {isPreviewing ? (
                    <Oval visible height="18" width="18" color="#0065FD" strokeWidth={8} secondaryColor="#E5E8F3" />
                  ) : (
                    <>
                      <HiOutlineEye size={16} />
                      Preview
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={sendReport}
                  disabled={isSending}
                  className={`h-10 px-4 rounded-[8px] bg-[#0065FD] flex items-center justify-center min-w-[120px] font-OpenSansSemiBold text-[16px] leading-[24px] text-white capitalize ${
                    isSending ? "cursor-not-allowed opacity-80" : "cursor-pointer hover:bg-[#0058E5]"
                  }`}
                >
                  {isSending ? (
                    <Oval visible height="18" width="18" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" />
                  ) : (
                    "Send Report"
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Preview body */}
              <div className="p-8 flex flex-col gap-8">
                <div className="rounded-[10px] bg-[#F8F9FE] outline outline-1 -outline-offset-1 outline-[#E5E8F3] p-6 flex flex-col gap-4">
                  <p className="font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#101828]">
                    Report Summary
                  </p>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <SummaryField label="Report Type" value={selectedType.name} />
                    <SummaryField label="Date Range" value={dateRangeLabel} />
                    <SummaryField label="Customer" value={filterSummary(filters.customerIds, "All Customers", "Customer")} />
                    <SummaryField label="Branch" value={filterSummary(filters.branchIds, "All Branches", "Branch")} />
                    <SummaryField label="Location" value={filterSummary(filters.locationIds, "All Locations", "Location")} />
                    <SummaryField label="Technician" value={filterSummary(filters.technicianIds, "All Technicians", "Technician")} />
                    <SummaryField label="Records Found" value={String(preview?.recordsFound ?? 0)} />
                  </div>
                  <div className="border-t-[1px] border-[#E5E8F3] pt-4 flex flex-col gap-2">
                    <p className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#7C829A]">
                      Recipients
                    </p>
                    <div className="flex flex-wrap content-center items-center gap-2">
                      {recipients.length === 0 ? (
                        <span className="font-OpenSansRegular text-[14px] text-[#868DA6]">No recipients selected</span>
                      ) : (
                        recipients.map((recipient, index) => (
                          <span
                            key={recipient.id ?? recipient.email ?? index}
                            className="flex items-center gap-1 pl-3 pr-2.5 py-1 rounded-[16px] bg-[#FBFCFF] outline outline-1 -outline-offset-1 outline-[#E5E8F3]"
                          >
                            <span className="text-center font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]">
                              {recipient.name && recipient.email
                                ? `${recipient.name} (${recipient.email})`
                                : recipient.email || recipient.name}
                            </span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  {/* Records table */}
                  <div className="rounded-[12px] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.06)] outline outline-1 -outline-offset-1 outline-[#E5E8F3] overflow-hidden">
                    {previewRows.length ? (
                      <PreviewTable columns={columns} rows={previewRows} />
                    ) : (
                      <div className="px-5 py-8 text-center font-OpenSansRegular text-[14px] text-[#868DA6]">
                        No records found for the selected filters.
                      </div>
                    )}
                  </div>
                  <p className="font-OpenSansRegular italic text-[14px] leading-[20px] text-[#7C829A]">
                    Preview only. Showing sample report data.
                  </p>
                </div>
              </div>

              {/* Preview footer */}
              <div className="flex items-center justify-end gap-4 px-6 py-4 border-t-[1px] border-[#E5E8F3]">
                <button
                  type="button"
                  onClick={() => setStep("filters")}
                  className="h-10 px-4 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] hover:bg-[#F7F9FC]"
                >
                  Back To Filters
                </button>
                <button
                  type="button"
                  onClick={sendReport}
                  disabled={isSending}
                  className={`h-10 px-4 rounded-[8px] bg-[#0065FD] flex items-center justify-center min-w-[130px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white ${
                    isSending ? "cursor-not-allowed opacity-80" : "hover:bg-[#0058E5]"
                  }`}
                >
                  {isSending ? (
                    <Oval visible height="18" width="18" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" />
                  ) : (
                    "Send Report"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </OutsideClickHandler>
    </div>
  );
};

const SummaryField = ({ label, value }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <p className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#7C829A]">
      {label}
    </p>
    <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
      {value}
    </p>
  </div>
);

// The preview table. Each header/data row is a CSS grid sharing one column
// template, so every cell in a row shares that row's height and the columns stay
// aligned even when a value (e.g. a long customer name) wraps to multiple lines.
const PreviewTable = ({ columns, rows }) => {
  const gridTemplateColumns = columns.map((column) => column.track).join(" ");
  return (
    <div>
      <div
        className="grid bg-[#F8F9FE] border-b-[1px] border-[#E5E8F3]"
        style={{ gridTemplateColumns }}
      >
        {columns.map((column) => (
          <div key={column.key} className="px-4 py-3 flex items-center">
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] text-[#5F6477]">{column.header}</span>
          </div>
        ))}
      </div>
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid border-b-[1px] border-[#E5E8F3] last:border-b-0"
          style={{ gridTemplateColumns }}
        >
          {columns.map((column) => (
            <div key={column.key} className="px-4 py-3 flex items-center min-w-0">
              <span
                className={`${
                  column.strong ? "font-OpenSansSemiBold text-[#1A2332]" : "font-OpenSansRegular text-[#5F6477]"
                } text-[14px] leading-[20px] ${column.truncate ? "truncate" : ""}`}
              >
                {row[column.key]}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default GenerateReportModal;
